import type { ReactNode } from 'react';
import {
  BellOutlined,
  CalendarOutlined,
  ContactsOutlined,
  EnvironmentOutlined,
  FolderOpenOutlined,
  FormOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SolutionOutlined,
  TagsOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/services';

export type TNavItem = {
  /** Route path; also used as the React key. */
  key: string;
  label: string;
  icon: ReactNode;
  roles: string[];
  /** Only highlight on an exact path match (root pages that every other route starts with). */
  exact?: boolean;
  /** Extra path prefixes that should keep this item highlighted. */
  matches?: string[];
};

const navItems: TNavItem[] = [
  // Admin
  {
    key: '/',
    label: 'Dashboard',
    icon: <HomeOutlined />,
    roles: ['ADMIN'],
    exact: true,
    matches: ['/insights'],
  },
  { key: '/clients', label: 'Clients', icon: <TeamOutlined />, roles: ['ADMIN'] },
  { key: '/advisors', label: 'Advisors', icon: <SolutionOutlined />, roles: ['ADMIN'] },
  { key: '/walkthroughs', label: 'Walkthroughs', icon: <CalendarOutlined />, roles: ['ADMIN'] },
  {
    key: '/help-requests',
    label: 'Help Requests',
    icon: <QuestionCircleOutlined />,
    roles: ['ADMIN'],
  },
  {
    key: '/survey-management',
    label: 'Survey Management',
    icon: <FormOutlined />,
    roles: ['ADMIN'],
  },
  { key: '/attributes', label: 'Global Attributes', icon: <TagsOutlined />, roles: ['ADMIN'] },
  { key: '/settings', label: 'Settings', icon: <SettingOutlined />, roles: ['ADMIN'] },
  { key: '/regional-settings', label: 'Regions', icon: <EnvironmentOutlined />, roles: ['ADMIN'] },

  // Client
  { key: '/client', label: 'Home', icon: <HomeOutlined />, roles: ['CLIENT'], exact: true },
  { key: '/client/survey', label: 'Property Survey', icon: <FormOutlined />, roles: ['CLIENT'] },
  {
    key: '/client/maintenance-tasks',
    label: 'Maintenance Tasks',
    icon: <ToolOutlined />,
    roles: ['CLIENT'],
  },
  { key: '/client/documents', label: 'Documents', icon: <FolderOpenOutlined />, roles: ['CLIENT'] },
  { key: '/client/calendar', label: 'Calendar', icon: <CalendarOutlined />, roles: ['CLIENT'] },
  { key: '/client/contacts', label: 'Contacts', icon: <ContactsOutlined />, roles: ['CLIENT'] },
  { key: '/client/profile', label: 'My Profile', icon: <UserOutlined />, roles: ['CLIENT'] },
  {
    key: '/client/notifications',
    label: 'Notifications',
    icon: <BellOutlined />,
    roles: ['CLIENT'],
  },
  { key: '/client/help', label: 'Get Help', icon: <QuestionCircleOutlined />, roles: ['CLIENT'] },

  // Advisor
  { key: '/advisor/clients', label: 'Clients', icon: <TeamOutlined />, roles: ['ADVISOR'] },
  {
    key: '/advisor/walkthroughs',
    label: 'My Walkthroughs',
    icon: <CalendarOutlined />,
    roles: ['ADVISOR'],
  },
];

export const isNavItemActive = (item: TNavItem, pathname: string) => {
  if (pathname === item.key) return true;
  if (!item.exact && pathname.startsWith(`${item.key}/`)) return true;
  return item.matches?.some((prefix) => pathname.startsWith(prefix)) ?? false;
};

export const useCurrentUser = () =>
  useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
  });

/** Nav items the signed-in user is allowed to see. */
export const useNavItems = () => {
  const { data: user } = useCurrentUser();

  const role = user?.role;
  const hasPaidPlan = !!user?.profile?.meet_paid;

  return navItems.filter((item) => {
    if (!role || !item.roles.includes(role)) return false;
    // Hide Property Survey after user has paid for the plan
    if (item.key === '/client/survey' && hasPaidPlan) return false;
    return true;
  });
};
